import { Component, Input, Output, EventEmitter, OnInit, DoCheck } from '@angular/core';
import { ChatMessage } from '../../models/chat.models';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { LinkifyPipe } from '../../pipes/linkify.pipe';
import { UtcDatePipe } from '../../pipes/utc-date.pipe';

interface MarkdownTable {
  headers: string[];
  rows: string[][];
}

@Component({
  selector: 'app-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  imports: [CommonModule, MatButtonModule, MatIconModule, MatChipsModule, LinkifyPipe, UtcDatePipe],
})
export class ChatMessageComponent implements OnInit, DoCheck {
  @Input() message!: ChatMessage;
  /** True when this is the last message in the list — enables quick-reply chips. */
  @Input() isLastMessage = false;
  /** Emitted when the user clicks a quick-reply chip. Parent should call sendMessage(). */
  @Output() quickReply = new EventEmitter<string>();

  parsedContent: { type: 'text' | 'table', content: any }[] = [];
  quickReplySuggestions: string[] = [];
  expandedSources = false;
  readonly MAX_VISIBLE_SOURCES = 5;
  private lastParsedContent = '';

  get visibleSources() {
    return this.expandedSources
      ? this.message.sources
      : this.message.sources.slice(0, this.MAX_VISIBLE_SOURCES);
  }

  constructor(private sanitizer: DomSanitizer, private router: Router) {}

  ngOnInit(): void {
    this.lastParsedContent = this.message.content;
    this.parseMessageContent();
  }

  ngDoCheck(): void {
    if (this.message && this.message.content !== this.lastParsedContent) {
      this.lastParsedContent = this.message.content;
      this.parsedContent = [];
      this.parseMessageContent();
    }
  }

  private parseMessageContent(): void {
    const content = this.message.content;

    // Extract quick-reply suggestions from bullet/numbered list items.
    // Only generate them for assistant messages so the user can click instead of type.
    if (this.message.role === 'Assistant') {
      this.quickReplySuggestions = this.extractListItems(content);
    } else {
      this.quickReplySuggestions = [];
    }

    const tableRegex = /\|(.+)\|[\r\n]+\|[-:\s|]+\|[\r\n]+((?:\|.+\|[\r\n]*)+)/g;
    
    let lastIndex = 0;
    let match;
    
    while ((match = tableRegex.exec(content)) !== null) {
      // Add text before table
      if (match.index > lastIndex) {
        const textContent = content.substring(lastIndex, match.index).trim();
        if (textContent) {
          this.parsedContent.push({ type: 'text', content: textContent });
        }
      }
      
      // Parse table
      const table = this.parseMarkdownTable(match[0]);
      if (table) {
        this.parsedContent.push({ type: 'table', content: table });
      }
      
      lastIndex = tableRegex.lastIndex;
    }
    
    // Add remaining text after last table
    if (lastIndex < content.length) {
      const textContent = content.substring(lastIndex).trim();
      if (textContent) {
        this.parsedContent.push({ type: 'text', content: textContent });
      }
    }
    
    // If no tables found, treat entire content as text
    if (this.parsedContent.length === 0) {
      this.parsedContent.push({ type: 'text', content: content });
    }
  }

  private parseMarkdownTable(tableText: string): MarkdownTable | null {
    const lines = tableText.split(/[\r\n]+/).filter(line => line.trim().startsWith('|'));
    
    if (lines.length < 3) return null; // Need header, separator, and at least one row
    
    const headers = lines[0].split('|').map(h => h.trim()).filter(h => h);
    const rows = lines.slice(2).map(line => 
      line.split('|').map(cell => cell.trim()).filter(cell => cell)
    );
    
    return { headers, rows };
  }

  openSource(url: string): void {
    if (url.startsWith('/')) {
      this.router.navigateByUrl(url);
    } else {
      window.open(url, '_blank');
    }
  }

  navigateTo(route: string): void {
    if (this.router.url.split('?')[0] === route) {
      window.location.reload();
      return;
    }

    void this.router.navigateByUrl(route);
  }

  getJobDetailsRoute(): string {
    const progress = this.message.jobProgress;
    if (progress?.trackedBusinessApplicationId != null) {
      return `/assessment/${progress.trackedBusinessApplicationId}`;
    }

    return `/admin/ai-assessment/job/${progress?.jobId}`;
  }

  toggleSources(): void {
    this.expandedSources = !this.expandedSources;
  }

  onQuickReply(text: string): void {
    this.quickReply.emit(text);
  }

  /**
   * Extract top-level list items from markdown content.
   * Returns up to 8 entries; only when the message is a question (ends with "?")
   * to avoid showing chips for every bulleted response.
   */
  private extractListItems(content: string): string[] {
    // Only show chips when the message poses a question.
    if (!content.includes('?')) return [];

    // Repository-selection prompts should only show actionable repo options,
    // not explanatory bullets (e.g. "full web link format" warnings).
    const isRepoSelectionPrompt = /which\s+repository\s+should\s+i\s+use/i.test(content)
      || /run\s+the\s+new\s+ai\s+code\s+analysis/i.test(content);

    const items: string[] = [];
    for (const line of content.split('\n')) {
      // Match "• text", "- text", "* text", "1. text", "1) text" etc.
      const m = line.match(/^\s*(?:[•\-\*]|\d+[.):])\s+(.+)/);
      if (m) {
        const text = m[1].trim()
          // Strip inline markdown bold/italic
          .replace(/\*\*/g, '').replace(/\*/g, '').replace(/__/g, '').replace(/_/g, '');

        if (isRepoSelectionPrompt) {
          // Skip non-actionable warning/help bullets.
          if (/appears\s+to\s+be\s+a\s+full\s+web\s+link/i.test(text)
              || /can'?t\s+use/i.test(text)) {
            continue;
          }

          const repo = this.tryExtractRepoPath(text);
          if (!repo) continue;

          const branch = this.tryExtractBranch(text) ?? 'master';
          items.push(`Use ${repo} on ${branch} branch`);
        } else if (text.length > 0 && text.length < 200) {
          items.push(text);
        }
      }
      if (items.length >= 8) break;
    }
    // Only return chips when there are 2-8 options (avoid noise for single-item lists)
    return items.length >= 2 ? items : [];
  }

  private tryExtractRepoPath(text: string): string | null {
    // Support the canonical "project/repo" format used by start_code_assessment.
    const repoMatch = text.match(/([A-Za-z0-9._-]+\/[A-Za-z0-9._-]+)/);
    return repoMatch?.[1] ?? null;
  }

  private tryExtractBranch(text: string): string | null {
    const branchMatch = text.match(/branch\s*:\s*([A-Za-z0-9._\/-]+)/i);
    return branchMatch?.[1] ?? null;
  }
}

